import { useRef, useEffect, useCallback } from 'react'
import { Appointment, TaskListItem, TechnicianAppointmentsItem } from '@myapp/contract'
import { CAL_START_HOUR, CAL_TOTAL_HEIGHT, CAL_HEADER_HEIGHT, HOUR_HEIGHT } from '../model/calendarConstants'
import { CalendarHeader } from './CalendarHeader'
import { CalendarTimeAxis } from './CalendarTimeAxis'
import { DayColumn } from './DayColumn'
import { UnscheduledTasksBanner } from './UnscheduledTasksBanner'
import { AppointmentDepartureReminderBanner, DepartureReminderBanner } from '../../../features/aufgaben/departure-reminder'
import { useCurrentTimeLine } from '../model/useCurrentTimeLine'
import { useBufferedCalendarData } from '../model/useBufferedCalendarData'
import { useCalendarBlockDrag, DragType, TapInfo } from '../model/useCalendarBlockDrag'
import { checkPauseCollisions } from '../model/checkPauseCollisions'
import { usePauseEditState } from '../model/pauseEditState'
import { PausePopover } from './PausePopover'
import { combineDayAndTimeIso } from '../../../shared/helpers/date'
import { resolveBreakForDay } from '../../../entities/time-entry/model/useBreakBlock'
import { useUpsertBreak } from '../../../entities/time-entry/api/mutations/useUpsertBreak'
import { useUpdateTimeManagement } from '../../../entities/time-entry/api/mutations/useUpdateTimeManagement'
import { useCalendarSwipe } from '../model/useCalendarSwipe'
import type { TimeManagementListItem } from '@myapp/contract'

const TIME_AXIS_WIDTH = 48 // px, w-12

interface Props {
  days: Date[]
  dayIsos: string[]
  todayIso: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
  technicianData: TechnicianAppointmentsItem | null
  currentUserId: number
  onAppointmentClick: (appointmentId: string) => void
  onTMBlockClick: (entryId: number) => void
  scheduledTasks?: TaskListItem[]
  unscheduledTasks?: TaskListItem[]
  onTaskClick?: (taskId: number) => void
  departureReminderTasks?: TaskListItem[]
  onDepartureTaskClick?: (taskId: number) => void
  appointmentDepartureReminderItems?: Appointment[]
  onAppointmentDepartureClick?: (appointmentId: string) => void
  isTransitioning?: boolean
}

export const WorkerCalendarWidget = ({
  days,
  dayIsos,
  todayIso,
  onPrev,
  onNext,
  onToday,
  technicianData,
  currentUserId,
  onAppointmentClick,
  onTMBlockClick,
  scheduledTasks = [],
  unscheduledTasks = [],
  onTaskClick,
  departureReminderTasks = [],
  onDepartureTaskClick,
  appointmentDepartureReminderItems = [],
  onAppointmentDepartureClick,
  isTransitioning = false,
}: Props) => {
  const currentTimePx = useCurrentTimeLine()
  const didScrollRef = useRef(false)

  const { scrollBodyRef, wrapperRef, wrapperStyle, isAnimating, triggerNext, triggerPrev } =
    useCalendarSwipe({ onNext, onPrev })

  const buffered = useBufferedCalendarData({
    dayIsos,
    technicianData,
    scheduledTasks,
    unscheduledTasks,
    departureReminderTasks,
    isTransitioning: isTransitioning || isAnimating,
  })

  const upsertBreak = useUpsertBreak()
  const updateTM = useUpdateTimeManagement()
  const pauseEdit = usePauseEditState()

  // Auto-scroll to current time on mount (once)
  useEffect(() => {
    if (didScrollRef.current) return
    const el = scrollBodyRef.current
    if (!el) return

    const now = new Date()
    const hourDecimal = now.getHours() + now.getMinutes() / 60
    const px = (hourDecimal - CAL_START_HOUR) * HOUR_HEIGHT
    el.scrollTop = Math.max(0, px - 150)
    didScrollRef.current = true
  }, [scrollBodyRef])

  const handleDrop = useCallback(
    (type: DragType, dayIso: string, hours: number, minutes: number, entryId?: number, anchorRect?: DOMRect) => {
      const td = buffered.technicianData
      if (!td) return

      const startIso = combineDayAndTimeIso(dayIso, hours, minutes)
      const startMs = new Date(startIso).getTime()

      if (type === 'break') {
        const endMs = startMs + 30 * 60_000
        const conflicts = checkPauseCollisions(
          { startMs, endMs, dayIso },
          td,
          { excludeBreak: true },
        )
        if (conflicts.length === 0) {
          upsertBreak.mutate({
            assignedTechnicianId: parseInt(td.technicianId, 10),
            date: dayIso,
            startTime: startIso,
          })
        } else {
          const endIso = new Date(endMs).toISOString()
          pauseEdit.openForBreak(dayIso, startIso, endIso, anchorRect ?? new DOMRect(), conflicts)
        }
      } else if (type === 'tm' && entryId !== undefined) {
        const entry = td.timeManagementEntries.find((e) => e.id === entryId)
        if (!entry) return
        const duration = new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()
        const endMs = startMs + duration
        const endIso = new Date(endMs).toISOString()
        const conflicts = checkPauseCollisions(
          { startMs, endMs, dayIso },
          td,
          { excludeTMId: entryId },
        )
        if (conflicts.length === 0) {
          updateTM.mutate({ id: entryId, dto: { startTime: startIso, endTime: endIso } })
        } else {
          pauseEdit.openForTM(entryId, dayIso, startIso, endIso, anchorRect ?? new DOMRect(), conflicts)
        }
      }
    },
    [buffered.technicianData, upsertBreak, updateTM, pauseEdit],
  )

  const handleTap = useCallback(
    ({ type, dayIso, entryId, anchorRect }: TapInfo) => {
      const td = buffered.technicianData
      if (!td) return

      if (type === 'break') {
        const breakBlock = resolveBreakForDay(td, dayIso)
        const startIso = breakBlock.startTimeIso
        const startMs = new Date(startIso).getTime()
        const endMs = startMs + 30 * 60_000
        const endIso = new Date(endMs).toISOString()
        const conflicts = checkPauseCollisions(
          { startMs, endMs, dayIso },
          td,
          { excludeBreak: true },
        )
        pauseEdit.openForBreak(dayIso, startIso, endIso, anchorRect, conflicts)
      } else if (type === 'tm' && entryId !== undefined) {
        const entry = td.timeManagementEntries.find((e) => e.id === entryId)
        if (!entry) return
        const startMs = new Date(entry.startTime).getTime()
        const endMs = new Date(entry.endTime).getTime()
        const conflicts = checkPauseCollisions(
          { startMs, endMs, dayIso },
          td,
          { excludeTMId: entryId },
        )
        pauseEdit.openForTM(entryId, dayIso, entry.startTime, entry.endTime, anchorRect, conflicts)
      }
    },
    [buffered.technicianData, pauseEdit],
  )

  const { drag, startDrag } = useCalendarBlockDrag(handleDrop, handleTap)

  // Prevent pinch-to-zoom while dragging
  useEffect(() => {
    if (!drag) return
    const preventZoom = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault() }
    const preventGesture = (e: Event) => e.preventDefault()
    document.addEventListener('touchstart', preventZoom, { passive: false })
    document.addEventListener('touchmove', preventZoom, { passive: false })
    document.addEventListener('gesturestart', preventGesture, { passive: false })
    document.addEventListener('gesturechange', preventGesture, { passive: false })
    return () => {
      document.removeEventListener('touchstart', preventZoom)
      document.removeEventListener('touchmove', preventZoom)
      document.removeEventListener('gesturestart', preventGesture)
      document.removeEventListener('gesturechange', preventGesture)
    }
  }, [drag])

  const onBreakPointerDown = useCallback(
    (dayIso: string, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => {
      startDrag('break', dayIso, e, gridEl, blockTopPx, undefined, anchorRect)
    },
    [startDrag],
  )

  const handlePopoverChange = useCallback(
    (startIso: string, endIso: string) => {
      const td = buffered.technicianData
      if (!td) return
      const editing = pauseEdit.editing
      if (!editing) return

      const startMs = new Date(startIso).getTime()
      const endMs = new Date(endIso).getTime()
      const { dayIso } = editing

      const conflicts = checkPauseCollisions(
        { startMs, endMs, dayIso },
        td,
        editing.kind === 'break'
          ? { excludeBreak: true }
          : { excludeTMId: editing.id },
      )
      pauseEdit.updateRange(startIso, endIso, conflicts)
    },
    [buffered.technicianData, pauseEdit],
  )

  const handlePopoverSave = useCallback(() => {
    const td = buffered.technicianData
    const editing = pauseEdit.editing
    if (!editing || !td) return

    if (editing.kind === 'break') {
      upsertBreak.mutate(
        {
          assignedTechnicianId: parseInt(td.technicianId, 10),
          date: editing.dayIso,
          startTime: editing.startIso,
        },
        { onSuccess: () => pauseEdit.close() },
      )
    } else if (editing.kind === 'tm' && editing.id !== undefined) {
      updateTM.mutate(
        { id: editing.id, dto: { startTime: editing.startIso, endTime: editing.endIso } },
        { onSuccess: () => pauseEdit.close() },
      )
    }
  }, [buffered.technicianData, pauseEdit, upsertBreak, updateTM])

  const isPending = upsertBreak.isPending || updateTM.isPending

  const onTMPointerDown = useCallback(
    (entry: TimeManagementListItem, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => {
      const dayIso = gridEl.dataset.dayIso ?? entry.startTime.slice(0, 10)
      startDrag('tm', dayIso, e, gridEl, blockTopPx, entry.id, anchorRect)
    },
    [startDrag],
  )

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Month nav — never scrolls */}
      <CalendarHeader days={days} onPrev={triggerPrev} onNext={triggerNext} onToday={onToday} />

      {/* Thin loading bar below header */}
      <div className="h-0.5 bg-transparent overflow-hidden shrink-0">
        {isTransitioning && (
          <div className="h-full bg-blue-400 animate-pulse w-full" />
        )}
      </div>

      {/* Unscheduled tasks banner */}
      <UnscheduledTasksBanner tasks={buffered.unscheduledTasks} onTaskClick={(id) => onTaskClick?.(id)} />

      {/* Departure reminder banners */}
      <DepartureReminderBanner tasks={buffered.departureReminderTasks} onTaskClick={(id) => onDepartureTaskClick?.(id)} />
      <AppointmentDepartureReminderBanner appointments={appointmentDepartureReminderItems} onAppointmentClick={(id) => onAppointmentDepartureClick?.(id)} />

      {/* Scrollable body */}
      <div ref={scrollBodyRef} className="flex-1 overflow-y-auto overflow-x-hidden touch-pan-y">
        <div
          className={`flex transition-opacity duration-200 ${isTransitioning && !isAnimating ? 'opacity-60' : 'opacity-100'}`}
          style={{ minHeight: CAL_TOTAL_HEIGHT }}
        >
          {/* Time axis */}
          <div
            className="shrink-0 border-r border-gray-200 bg-white"
            style={{ width: TIME_AXIS_WIDTH }}
          >
            <div className="sticky top-0 z-50 bg-white border-b border-gray-200" style={{ height: CAL_HEADER_HEIGHT }} />
            <CalendarTimeAxis />
          </div>

          {/* Day columns — animated on swipe */}
          <div
            ref={wrapperRef}
            className="flex flex-1 min-w-0"
            style={wrapperStyle}
          >
            {buffered.dayIsos.map((iso) => (
              <DayColumn
                key={iso}
                dayIso={iso}
                isToday={iso === todayIso}
                technicianData={buffered.technicianData}
                currentTimePx={currentTimePx}
                currentUserId={currentUserId}
                onAppointmentClick={onAppointmentClick}
                onTMBlockClick={onTMBlockClick}
                scheduledTasks={buffered.scheduledTasks}
                onTaskClick={onTaskClick}
                drag={drag}
                onBreakPointerDown={onBreakPointerDown}
                onTMPointerDown={onTMPointerDown}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Pause popover — portal, rendered once at widget level */}
      {pauseEdit.editing && (
        <PausePopover
          anchorRect={pauseEdit.editing.anchorRect}
          startIso={pauseEdit.editing.startIso}
          endIso={pauseEdit.editing.endIso}
          conflicts={pauseEdit.editing.conflicts}
          isPending={isPending}
          fixedDurationMin={pauseEdit.editing.kind === 'break' ? 30 : undefined}
          onChange={handlePopoverChange}
          onSave={handlePopoverSave}
          onCancel={pauseEdit.close}
        />
      )}
    </div>
  )
}
